import React, { useMemo, useState } from 'react';
import { MapPinIcon, PhoneIcon, ShieldAlertIcon, ShieldCheckIcon, TriangleAlertIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { EmptyState } from '../../components/ui/EmptyState';
import { Textarea } from '../../components/ui/Field';
import { useSafety } from '../../contexts/SafetyContext';
import { campusResponders } from '../../data/safety';
import type { SafetyIncident } from '../../types/safety';
const TABS = ['Live alerts', 'Incident reports', 'Closed', 'Response readiness'] as const;

/** The responder signed in at this desk. Every action is attributed to them. */
const DESK_OFFICER = 'Sgt. E. Bassey, UNILAG Security Command';
const DESK_CAMPUS = 'campus-akoka';
export function SafetyDesk() {
  const {
    alerts,
    incidents,
    acknowledge,
    markResponding,
    close,
    updateIncident
  } = useSafety();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Live alerts');
  const [outcomes, setOutcomes] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const mine = useMemo(() => alerts.filter(alert => alert.campusId === DESK_CAMPUS), [alerts]);
  const live = mine.filter(alert => !['closed', 'cancelled'].includes(alert.state));
  const done = mine.filter(alert => ['closed', 'cancelled'].includes(alert.state));
  const elsewhere = alerts.length - mine.length;
  return <div className="space-y-6" data-id="element-8928" data-mp="2xw29lseo0gdd">
      <PageHeader eyebrow="Safety" title="Campus safety desk" description="Alerts and reports from students on this campus. Everything you do here is attributed to you and cannot be edited afterwards." actions={<Badge tone={live.length > 0 ? 'danger' : 'ok'} data-id="element-8930" data-mp="2a70ujmbbu6z4">
            {live.length > 0 ? `${live.length} open` : 'Nothing open'}
          </Badge>} data-id="element-8929" data-mp="2xvsfptqi1p2e" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Safety desk sections" data-id="element-8931" data-mp="3dg3mgblioosw" />

      {tab === 'Live alerts' && <div className="space-y-5" data-id="element-8932" data-mp="3maibh5vundp7">
          {live.length === 0 ? <EmptyState icon={<ShieldCheckIcon className="h-6 w-6" data-id="element-8934" data-mp="3i8n0f9o49ly8" />} title="No open alerts" description="This is the correct state most of the time. Alerts raised by students on this campus arrive here immediately and this desk is the record of what happened next." data-id="element-8933" data-mp="3mcuuznh99w4d" /> : live.map(alert => <Panel key={alert.id} title={`${alert.reference} · ${alert.raisedBy.name}`} description={`Raised ${alert.raisedAt} · @${alert.raisedBy.handle}`} action={<Badge tone={alert.state === 'delivered' ? 'danger' : 'brand'} data-id="element-8936" data-mp="duynomdy6nhj">
                    {alert.state === 'delivered' ? 'Unopened' : alert.state === 'recorded' ? 'Relayed' : alert.state === 'acknowledged' ? 'Acknowledged' : 'Responding'}
                  </Badge>} data-id="element-8935" data-mp="33lttaror3mh4">
                <div className="space-y-4" data-id="element-8937" data-mp="hdnq6kc2w479">
                  <p className="max-w-[74ch] text-body text-ink" data-id="element-8938" data-mp="152gogng6ao51">
                    {alert.note || 'The student did not add a note.'}
                  </p>

                  <dl className="grid gap-3 sm:grid-cols-2" data-id="element-8939" data-mp="1a10b6q2lc3yg">
                    <div data-id="element-8940" data-mp="vd5jnd0bk5my">
                      <dt className="text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-8941" data-mp="vbudx7nbcnt7">
                        Where
                      </dt>
                      <dd className="mt-0.5 flex items-start gap-2 text-body-sm text-ink" data-id="element-8942" data-mp="1o1kudncf41sh">
                        <MapPinIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-8943" data-mp="1ss1axgbjc3j2" />
                        <span data-id="element-8944" data-mp="45ykpzt1go4e">
                          {alert.placeHint || 'Not described'}
                          {alert.coordinates ? <span className="cz-num block text-ink-muted" data-id="element-8945" data-mp="3tp4vj15hf4">
                              {alert.coordinates.lat.toFixed(4)}, {alert.coordinates.lng.toFixed(4)}{' '}
                              (±{alert.coordinates.accuracyMetres} m)
                            </span> : <span className="block text-ink-muted" data-id="element-8946" data-mp="ntefw0lfdiyy">
                              Precise location withheld by the student.
                            </span>}
                        </span>
                      </dd>
                    </div>
                    <div data-id="element-8947" data-mp="94ry7s8bsf4k">
                      <dt className="text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-8948" data-mp="93gvlvqm9df1">
                        Trusted contacts
                      </dt>
                      <dd className="mt-0.5 text-body-sm text-ink" data-id="element-8949" data-mp="11se3d4x7brtj">
                        {alert.trustedContactsNotified.length > 0 ? alert.trustedContactsNotified.join(', ') : 'Not shared. Do not attempt to reach their family through other means.'}
                      </dd>
                    </div>
                  </dl>

                  <div className="rounded-md border border-line bg-canvas px-3.5 py-3" data-id="element-8950" data-mp="1o3fcw2wxkxzq">
                    <p className="text-body-sm font-semibold text-ink" data-id="element-8951" data-mp="1s4r60if0k1co">What this desk can see</p>
                    <p className="mt-1 max-w-[72ch] text-body-sm text-ink-muted" data-id="element-8952" data-mp="1x2am7nv9uo79">
                      Only what the student chose to share on this alert. Their notes, diary,
                      results, wellbeing records, balances and messages are not available here and
                      are not withheld by this screen — they are absent from the alert itself.
                    </p>
                  </div>

                  <ol className="space-y-2 border-l-2 border-line pl-4" data-id="element-8953" data-mp="2fud575vdygko">
                    {alert.timeline.map((event, index) => <li key={index} data-id="element-8954" data-mp="gmpfb06c40xc" data-mpi={index}>
                        <p className="text-body-sm text-ink" data-id="element-8955" data-mp="bqiajapocvgz">{event.label}</p>
                        <p className="cz-num text-caption text-ink-subtle" data-id="element-8956" data-mp="ph3yhdtzjvlq">
                          {event.at} · {event.actor}
                        </p>
                      </li>)}
                  </ol>

                  {alert.state === 'delivered' || alert.state === 'recorded' ? <Button onClick={() => acknowledge(alert.id, DESK_OFFICER)} data-id="element-8957" data-mp="ugq9dgmcczrd">
                      Acknowledge — I have this
                    </Button> : <div className="space-y-3 border-t border-line pt-4" data-id="element-8958" data-mp="14eeff2pedtou">
                      <Textarea rows={2} aria-label="Response update" placeholder="Patrol dispatched from the main gate" value={notes[alert.id] ?? ''} onChange={event => setNotes({
              ...notes,
              [alert.id]: event.target.value
            })} data-id="element-8959" data-mp="14ca2xwdfnmcy" />
                      <div className="flex flex-wrap gap-2" data-id="element-8960" data-mp="2mc0zihxy17bk">
                        <Button variant="secondary" disabled={(notes[alert.id] ?? '').trim().length < 4} onClick={() => {
                markResponding(alert.id, DESK_OFFICER, notes[alert.id]);
                setNotes({
                  ...notes,
                  [alert.id]: ''
                });
              }} data-id="element-8961" data-mp="2mbhshw9u03cb">
                          Add update
                        </Button>
                      </div>

                      <Textarea rows={3} aria-label="Closing outcome" placeholder="What actually happened, and anything that needs fixing afterwards" value={outcomes[alert.id] ?? ''} onChange={event => setOutcomes({
              ...outcomes,
              [alert.id]: event.target.value
            })} data-id="element-8962" data-mp="28ci6kwdpe2ju" />
                      <Button disabled={(outcomes[alert.id] ?? '').trim().length < 12} onClick={() => close(alert.id, DESK_OFFICER, outcomes[alert.id])} data-id="element-8963" data-mp="15epn854hpfcg">
                        Close with outcome
                      </Button>
                      <p className="text-caption text-ink-subtle" data-id="element-8964" data-mp="rfgji4xh0dpd">
                        An alert cannot be closed silently. The outcome you write is shown to the
                        student.
                      </p>
                    </div>}
                </div>
              </Panel>)}

          {elsewhere > 0 && <p className="text-body-sm text-ink-muted" data-id="element-8965" data-mp="2ct2upjsl39is">
              {elsewhere} alert{elsewhere === 1 ? '' : 's'} exist on other campuses. This desk is
              scoped to {campusResponders[0].campusName} and cannot open them.
            </p>}
        </div>}

      {tab === 'Incident reports' && <div className="space-y-5" data-id="element-8966" data-mp="8al8lkh8634v">
          {incidents.filter(incident => !['Resolved', 'Closed — no action'].includes(incident.status)).map(incident => <IncidentRow key={incident.id} incident={incident} onUpdate={updateIncident} data-id="element-8967" data-mp="3bs3gtt0wtc5t" />)}
        </div>}

      {tab === 'Closed' && <div className="space-y-5" data-id="element-8968" data-mp="3gr6jj6tf36am">
          {done.length === 0 ? <EmptyState icon={<ShieldAlertIcon className="h-6 w-6" data-id="element-8970" data-mp="3kumfe0xnx2m9" />} title="Nothing closed yet" description="Closed alerts stay here permanently with their outcome and full timeline." data-id="element-8969" data-mp="3krgsmsciactp" /> : done.map(alert => <Panel key={alert.id} title={`${alert.reference} · ${alert.raisedBy.name}`} description={`${alert.raisedAt} · handled by ${alert.responder ?? 'nobody'}`} data-id="element-8971" data-mp="150ceefuyvcpz">
                <p className="max-w-[74ch] text-body-sm text-ink" data-id="element-8972" data-mp="3soparzczwhvk">
                  {alert.outcome ?? 'Withdrawn by the student before a responder acted.'}
                </p>
              </Panel>)}
        </div>}

      {tab === 'Response readiness' && <div className="space-y-5" data-id="element-8973" data-mp="34u5x8p9c64k5">
          <Panel title="Campuses and how alerts reach them" description="A campus with no agreement cannot receive alerts. This is stated to students before they raise one." bodyClassName="p-0" data-id="element-8974" data-mp="39ro7ciz3aptq">
            <ul className="divide-y divide-line" data-id="element-8975" data-mp="3itf8rbmlkahs">
              {campusResponders.map(responder => <li key={responder.campusId} className="px-5 py-4 sm:px-6" data-id="element-8976" data-mp="tzrzh17ik22d">
                  <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-8977" data-mp="y1djd6igtgm2">
                    <div className="min-w-0" data-id="element-8978" data-mp="2ijxpsexmezpr">
                      <p className="text-body font-semibold text-ink" data-id="element-8979" data-mp="2ijo2x07vh8oa">{responder.unit}</p>
                      <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-8980" data-mp="2ejvs0gv0aqfr">
                        {responder.institutionName} · {responder.campusName}
                      </p>
                      <p className="cz-num mt-1 flex items-center gap-1.5 text-body-sm text-brand-700" data-id="element-8981" data-mp="24tkj0fwgtr20">
                        <PhoneIcon aria-hidden className="h-3.5 w-3.5" data-id="element-8982" data-mp="1zur5inh6i01u" />
                        {responder.phone}
                      </p>
                      <p className="mt-1 text-body-sm text-ink-muted" data-id="element-8983" data-mp="1uwpmu3no02qw">{responder.hours}</p>
                    </div>
                    <div className="shrink-0 text-right" data-id="element-8984" data-mp="1quul4y1tnncl">
                      <Badge tone={responder.channel === 'console' ? 'ok' : responder.channel === 'relay' ? 'warn' : 'danger'} data-id="element-8985" data-mp="1lv874me6632q">
                        {responder.channel === 'console' ? 'Works this desk' : responder.channel === 'relay' ? 'Relayed by phone' : 'No agreement'}
                      </Badge>
                      <p className="cz-num mt-1.5 text-body-sm text-ink-muted" data-id="element-8986" data-mp="3v8v3gn9kizow">
                        {responder.medianAckMinutes === null ? 'No data' : `${responder.medianAckMinutes} min median`}
                      </p>
                      <p className="text-caption text-ink-subtle" data-id="element-8987" data-mp="cwgibo74qf4j">
                        {responder.agreementConfirmed ? `Confirmed ${responder.agreementConfirmed}` : 'Not confirmed'}
                      </p>
                    </div>
                  </div>
                </li>)}
            </ul>
          </Panel>

          <Panel title="What this desk is not" data-id="element-8988" data-mp="2v1gbodxhdo36">
            <ul className="space-y-2.5" data-id="element-8989" data-mp="2q3okxn8ko80k">
              {['It is not an emergency service. A phone call reaches a person faster, and students are told so before they raise an alert.', 'It does not track anyone. A location arrives only with an alert, only if the student shared it, and only for that alert.', 'It cannot reach a student’s notes, diary, wellbeing records, results, balances or messages. Those are absent from the alert, not hidden by this screen.', 'A median acknowledgement time is a measurement of past alerts, never a promise about the next one.'].map(line => <li key={line} className="flex items-start gap-2.5 text-body-sm text-ink" data-id="element-8990" data-mp="2gcbun02f9pwn">
                  <TriangleAlertIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-8991" data-mp="1smzj3tjmh5r6" />
                  {line}
                </li>)}
            </ul>
          </Panel>
        </div>}
    </div>;
}
function IncidentRow({
  incident,
  onUpdate
}: {
  incident: SafetyIncident;
  onUpdate: (id: string, status: SafetyIncident['status'], note: string, actor: string) => void;
}) {
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<SafetyIncident['status']>(incident.status);
  return <Panel title={`${incident.reference} · ${incident.category}`} description={`Reported ${incident.reported}${incident.anonymous ? ' · filed anonymously' : ''}`} action={<Badge tone={incident.status === 'Received' ? 'warn' : 'brand'} data-id="element-8993" data-mp="2p3bzo3qivoji">{incident.status}</Badge>} data-id="element-8992" data-mp="2k2fvrq6682e3">
      <div className="space-y-4" data-id="element-8994" data-mp="2tyrbkq4yzada">
        <p className="max-w-[74ch] text-body-sm text-ink" data-id="element-8995" data-mp="2z087e78lk6n0">{incident.detail}</p>
        <p className="text-body-sm text-ink-muted" data-id="element-8996" data-mp="zmkr10q6rh8b">{incident.location}</p>
        {incident.anonymous && <p className="rounded-md border border-line bg-canvas px-3.5 py-3 text-body-sm text-ink-muted" data-id="element-8997" data-mp="14le62xl4fgal">
            Filed anonymously. You cannot follow up with the reporter, and Campuzon will not reveal
            who they are on request.
          </p>}

        <div className="space-y-3 border-t border-line pt-4" data-id="element-8998" data-mp="3d349316f8q2n">
          <div className="flex flex-wrap gap-2" data-id="element-8999" data-mp="394eyfgcrpmmp">
            {(['Under review', 'Action taken', 'Resolved', 'Closed — no action'] as const).map(option => <button key={option} type="button" onClick={() => setStatus(option)} className={`min-h-touch rounded-md border px-3.5 text-body-sm font-medium transition-colors duration-150 ease-out ${status === option ? 'border-brand-700 bg-brand-50 text-ink' : 'border-line-strong text-ink-muted hover:text-ink'}`} data-id="element-9000" data-mp="4powlkor015a">
                  {option}
                </button>)}
          </div>
          <Textarea rows={3} aria-label="Note shown to the reporter" placeholder="What you did about it. This is shown to the reporter." value={note} onChange={event => setNote(event.target.value)} data-id="element-9001" data-mp="2cxa9iat6elbm" />
          <Button disabled={note.trim().length < 12 || status === incident.status} onClick={() => onUpdate(incident.id, status, note, DESK_OFFICER)} data-id="element-9002" data-mp="23yoswl79lgdh">
            Record decision
          </Button>
          <p className="text-caption text-ink-subtle" data-id="element-9003" data-mp="1yxsqkc2ka2a6">
            A status change needs a reason of substance. Reports that change state silently teach
            students not to report.
          </p>
        </div>
      </div>
    </Panel>;
}